import { Component, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { finalize } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MaterialModule } from 'src/app/material.module';
import { IconModule } from 'src/app/shared/icon/icon.module';
import { SharedModule } from 'src/app/shared/shared.module';
import {
  RoleApiService,
  type PermissionRecord,
  type PermissionsGrouped,
  type RoleRow,
} from 'src/app/core/services/role-api.service';
import { isSuperAdminRoleSlug } from 'src/app/core/constants/super-admin-role';
import {
  ROLE_PERMISSION_ATTACH_SLUGS,
  SUPER_ADMIN_PERMISSION_SLUGS,
} from 'src/app/core/constants/super-admin-permission-slugs';
import { HasPermissionPipe } from 'src/app/core/pipes/has-permission.pipe';
import { AuditHistoryComponent } from 'src/app/shared/audit-history/audit-history.component';

interface PermissionEntry {
  id: number;
  name: string;
  slug: string;
  module: string;
  module_slug: string;
  selected: boolean;
}

interface PermissionGroup {
  module: string;
  module_slug: string;
  permissions: PermissionEntry[];
  selectAll: boolean;
}

@Component({
  selector: 'app-manage-role-permissions',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    SharedModule,
    MaterialModule,
    IconModule,
    HasPermissionPipe,
    AuditHistoryComponent,
  ],
  templateUrl: './manage-role-permissions.component.html',
})
export class ManageRolePermissionsComponent implements OnInit {
  readonly P = SUPER_ADMIN_PERMISSION_SLUGS;
  readonly roleAttachSlugs: string[] = [...ROLE_PERMISSION_ATTACH_SLUGS];

  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly roleApi = inject(RoleApiService);
  private readonly snackBar = inject(MatSnackBar);

  roleId = 0;
  role: RoleRow | null = null;

  loading = signal(false);
  saving = signal(false);

  groups: PermissionGroup[] = [];
  filterModule = '';
  selectAllGlobal = false;

  get moduleOptions(): string[] {
    return this.groups.map((g) => g.module);
  }

  get filteredGroups(): PermissionGroup[] {
    if (!this.filterModule) return this.groups;
    return this.groups.filter((g) => g.module === this.filterModule);
  }

  get selectedCount(): number {
    return this.groups.flatMap((g) => g.permissions).filter((p) => p.selected).length;
  }

  get totalCount(): number {
    return this.groups.flatMap((g) => g.permissions).length;
  }

  ngOnInit(): void {
    this.roleId = Number(this.route.snapshot.paramMap.get('roleId'));
    if (!Number.isFinite(this.roleId) || this.roleId < 1) {
      void this.router.navigate(['/system-preferences']);
      return;
    }
    const st = history.state as { role?: RoleRow };
    this.role = st?.role ?? null;
    if (this.role && isSuperAdminRoleSlug(this.role.slug)) {
      this.blockProtectedRole();
      return;
    }
    if (!this.role) {
      this.roleApi.getRoleById(this.roleId).subscribe({
        next: (res) => {
          if (res?.data) {
            this.role = res.data;
            if (isSuperAdminRoleSlug(this.role.slug)) {
              this.blockProtectedRole();
              return;
            }
          }
          this.loadData();
        },
        error: () => void this.router.navigate(['/system-preferences']),
      });
      return;
    }
    this.loadData();
  }

  private blockProtectedRole(): void {
    this.showToast('Super Admin role permissions cannot be modified.', 'error');
    void this.router.navigate(['/system-preferences']);
  }

  loadData(): void {
    this.loading.set(true);

    let catalog: PermissionsGrouped | null = null;
    let selectedIds = new Set<number>();
    let done = 0;

    const checkDone = () => {
      done++;
      if (done === 2 && catalog !== null) {
        this.buildGroups(catalog, selectedIds);
        this.loading.set(false);
      }
    };

    this.roleApi.fetchAllPermissions().subscribe({
      next: (res) => {
        const raw = res?.data;
        catalog =
          raw && typeof raw === 'object' && !Array.isArray(raw) ? raw : {};
        checkDone();
      },
      error: () => {
        catalog = {};
        checkDone();
      },
    });

    this.roleApi.fetchRolePermissions(this.roleId).subscribe({
      next: (res) => {
        const ids = res?.data?.permission_ids ?? [];
        selectedIds = new Set(
          (Array.isArray(ids) ? ids : []).map(Number).filter((n) => Number.isFinite(n) && n > 0),
        );
        checkDone();
      },
      error: () => {
        selectedIds = new Set();
        checkDone();
      },
    });
  }

  buildGroups(data: PermissionsGrouped, selected: Set<number>): void {
    this.groups = Object.entries(data).map(([moduleKey, permissions]) => {
      const entries: PermissionEntry[] = (permissions ?? []).map((p: PermissionRecord) => ({
        id: p.id,
        name: p.permissions_name ?? p.slug,
        slug: p.slug,
        module: p.model ?? moduleKey,
        module_slug: moduleKey,
        selected: selected.has(p.id),
      }));

      return {
        module: this.formatModuleName(moduleKey),
        module_slug: moduleKey,
        permissions: entries,
        selectAll: entries.length > 0 && entries.every((e) => e.selected),
      };
    });

    this.updateGlobalSelectAll();
  }

  private formatModuleName(key: string): string {
    return key.replace(/[-_]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  }

  updateGroupSelectAll(group: PermissionGroup): void {
    group.selectAll =
      group.permissions.length > 0 && group.permissions.every((p) => p.selected);
  }

  updateGlobalSelectAll(): void {
    this.selectAllGlobal =
      this.groups.length > 0 && this.groups.every((g) => g.selectAll);
  }

  onGlobalSelectAll(checked: boolean): void {
    this.selectAllGlobal = checked;
    this.groups.forEach((g) => {
      g.permissions.forEach((p) => {
        p.selected = checked;
      });
      g.selectAll = checked;
    });
  }

  onGroupSelectAll(group: PermissionGroup, checked: boolean): void {
    group.selectAll = checked;
    group.permissions.forEach((p) => {
      p.selected = checked;
    });
    this.updateGlobalSelectAll();
  }

  onPermissionChange(group: PermissionGroup): void {
    this.updateGroupSelectAll(group);
    this.updateGlobalSelectAll();
  }

  onSave(): void {
    if (this.role && isSuperAdminRoleSlug(this.role.slug)) {
      this.blockProtectedRole();
      return;
    }
    const selectedIds = this.groups
      .flatMap((g) => g.permissions.filter((p) => p.selected).map((p) => p.id));
    this.saving.set(true);
    this.roleApi
      .updateRolePermissions(this.roleId, selectedIds)
      .pipe(finalize(() => this.saving.set(false)))
      .subscribe({
        next: (res) => {
          this.showToast(res?.message || 'Permissions saved.');
          void this.router.navigate(['/system-preferences']);
        },
        error: (e) => this.showToast(this.parseHttpError(e), 'error'),
      });
  }

  onBack(): void {
    void this.router.navigate(['/system-preferences']);
  }

  private showToast(message: string, type: 'success' | 'error' = 'success'): void {
    this.snackBar.open(message, 'Close', { duration: 4000 });
  }

  private parseHttpError(err: unknown): string {
    if (err instanceof HttpErrorResponse) {
      const body = err.error;
      if (body && typeof body === 'object' && 'message' in body) {
        const m = (body as { message: unknown }).message;
        if (typeof m === 'string') return m;
        if (Array.isArray(m)) return m.join(', ');
      }
      return err.message || 'Request failed';
    }
    if (err instanceof Error) return err.message;
    return 'Request failed';
  }
}
